import { useLocalization } from "../../hooks/useLocalization";
import { RoutePageMeta } from "../../seo/RoutePageMeta";
import { Localization } from "../../types/types";
import { IGalleryItem } from "../../types/types";
import { GalleryContents } from "../../assets/objects/GalleryList";
import { ExternalLink } from "lucide-react";

const GALLERY_CARD_TEXT_FONT =
  "[font-family:'Inter',_-apple-system,_BlinkMacSystemFont,_sans-serif]";

const GALLERY_SLANT_VARIANTS = [
  {
    wrapper:
      "left-0 z-[3] w-[42%] [clip-path:polygon(0_0,100%_0,80%_100%,0_100%)] max-[1024px]:relative max-[1024px]:left-auto max-[1024px]:w-1/3 max-[1024px]:[clip-path:none] max-[600px]:w-full",
    overlay: "bg-[rgba(0,110,255,0.3)]",
  },
  {
    wrapper:
      "left-[32%] z-[2] w-[45%] [clip-path:polygon(20%_0,100%_0,80%_100%,0_100%)] max-[1024px]:relative max-[1024px]:left-auto max-[1024px]:w-1/3 max-[1024px]:[clip-path:none] max-[600px]:hidden",
    overlay: "bg-[rgba(0,110,255,0.55)]",
  },
  {
    wrapper:
      "right-0 z-[1] w-[41%] [clip-path:polygon(20%_0,100%_0,100%_100%,0_100%)] max-[1024px]:relative max-[1024px]:right-auto max-[1024px]:w-1/3 max-[1024px]:[clip-path:none] max-[600px]:hidden",
    overlay: "bg-[rgba(0,50,180,0.75)]",
  },
];

const GalleryCard = ({
  item,
  localization,
}: {
  item: IGalleryItem;
  localization: Localization;
}) => {
  const imgsBaseUrl = import.meta.env.VITE_S3_ASSET_URL;
  const displayName = localization === Localization.Finnish ? item.titleFi : item.titleEn;
  const displayDate = localization === Localization.Finnish ? item.dateFi : item.dateEn;
  const { strings } = useLocalization();
  return (
    <article className="group relative mb-8 w-full max-w-[80rem] cursor-pointer overflow-hidden rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] p-0 shadow-[0_4px_20px_rgba(0,0,0,0.4)] transition-[transform,box-shadow,border-color] duration-500 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:-translate-y-2 hover:border-[var(--surface-card-hover-border)] hover:shadow-[0_25px_60px_rgba(0,0,0,0.6)] max-[1024px]:mx-auto max-[1024px]:my-16 max-[1024px]:w-[95vw]">
      <h2
        className={`mx-8 mt-6 mb-4 text-left text-[0.95rem] font-bold uppercase tracking-[1px] text-[#888899] max-[1024px]:mx-6 max-[600px]:text-[1.1rem] ${GALLERY_CARD_TEXT_FONT}`}
      >
        {displayName}
      </h2>

      <div className="relative flex h-[380px] overflow-hidden bg-[var(--surface-card-bg)] max-[1024px]:h-[300px] max-[1024px]:gap-[2px] max-[1024px]:bg-transparent max-[600px]:h-[250px]">
        {item.image.slice(0, 3).map((imgName, index) => {
          const variant = GALLERY_SLANT_VARIANTS[index];
          return (
            <div key={index} className={`absolute top-0 h-full cursor-default ${variant.wrapper}`}>
              <img
                src={`${imgsBaseUrl}/images/${imgName}`}
                alt={`${displayName} view ${String(index + 1)}`}
                width={640}
                height={480}
                loading="lazy"
                decoding="async"
                className="block h-full w-full object-cover [filter:brightness(0.85)] [transition:filter_0.5s_ease] group-hover:[filter:brightness(1.2)]"
              />

              <div className={`absolute inset-0 z-[2] pointer-events-none ${variant.overlay}`} />

              {index === 0 && (
                <a
                  href={item.link}
                  className="absolute bottom-[25px] left-[25px] z-10 flex items-center gap-2 rounded border border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.1)] px-[18px] py-[10px] text-[0.75rem] font-semibold uppercase text-white no-underline backdrop-blur-[10px] transition-all duration-300 ease-in-out hover:border-[rgba(255,255,255,0.5)] hover:bg-[rgba(255,255,255,0.25)] max-[1024px]:bottom-[15px] max-[1024px]:left-[15px] max-[1024px]:px-[14px] max-[1024px]:py-[8px] max-[1024px]:text-[0.7rem]"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {strings.GALLERY.VIEW_MORE}
                  <ExternalLink size={14} className="ml-2" aria-hidden="true" />
                </a>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-8 py-[1.2rem] text-left max-[1024px]:px-6">
        <p className={`text-[0.95rem] text-[#888899] ${GALLERY_CARD_TEXT_FONT}`}>
          {displayDate} | @ {item.place}
        </p>
      </div>
    </article>
  );
};

export const Gallery = () => {
  const { locale, strings } = useLocalization();

  return (
    <div className="mt-48">
      <RoutePageMeta page="GALLERY" />
      <section className="flex w-full flex-col items-center" aria-labelledby="gallery-page-title">
        <h1
          id="gallery-page-title"
          className="mb-6 w-[min(92%,80rem)] text-center font-shentox text-[clamp(1.25rem,5vw,1.5rem)] font-bold uppercase leading-[1.2] tracking-[0.06em] text-light-blue"
        >
          {strings.SEO.GALLERY.TITLE}
        </h1>
        {GalleryContents.length > 0 && (
          <div className="flex w-[min(92%,80rem)] flex-col items-center">
            {GalleryContents.map((item, i) => (
              <GalleryCard key={i} item={item} localization={locale} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
